/**
 * The Sunday recap: a small copy of each GiviSnap, kept past its two days for
 * one collage of the week, then gone with it.
 *
 * Every morning the day before's GiviSnaps are copied small into recap/ and
 * listed on the week's recap (functions/src/recap.ts); on Sunday the week is
 * shown and the family told, and the week before is taken down. The
 * originals still go at two days, as every GiviSnap does.
 *
 * A week is named for the Sunday it is shown on, and holds the seven days
 * before it: Sunday to Saturday.
 */
import { DAILY_NAME } from './constants';

export const RECAP_NAME = 'This week in the Den';

/** Fewer than this and there is no collage to make; the week passes quietly. */
export const RECAP_MIN_POSTS = 3;

/** How wide the kept copy is: a collage tile, not a photograph. */
export const RECAP_WIDTH = 720;

/**
 * How long R2 keeps a copy (r2-lifecycle.json): long enough for Sunday's
 * GiviSnap, copied on Monday, to last through the week it is shown in.
 */
export const RECAP_KEEP_DAYS = 15;

export interface RecapEntry {
  /** The small copy, under recap/. */
  key: string;
  authorUid: string;
  /** The day it answered, 'yyyy-mm-dd'. */
  day: string;
  /** When it was posted, in ms. */
  at: number;
  width: number;
  height: number;
}

/** recaps/{week}: the week's copies as they are kept, and whether it is out. */
export interface RecapDoc {
  week: string;
  entries: RecapEntry[];
  /** Set on its Sunday, when there were enough to show. */
  ready?: boolean;
}

const DAY_MS = 86_400_000;

function noonOf(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d, 12);
}

function keyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function isSunday(day: string): boolean {
  return new Date(noonOf(day)).getUTCDay() === 0;
}

/** The Sunday a day's GiviSnaps are shown on: the next one after it. */
export function recapWeekOf(day: string): string {
  const weekday = new Date(noonOf(day)).getUTCDay();
  return keyOf(noonOf(day) + (7 - weekday) * DAY_MS);
}

/** The first and last days a week holds: the Sunday before, to Saturday. */
export function weekSpan(week: string): { from: string; to: string } {
  return { from: keyOf(noonOf(week) - 7 * DAY_MS), to: keyOf(noonOf(week) - DAY_MS) };
}

/** Where a GiviSnap's copy is kept. */
export function recapKey(week: string, day: string, postId: string): string {
  return `recap/${week}/${day}-${postId}.jpg`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 12 – 18", or "Sep 28 – Oct 4" across a month. */
export function weekWords(week: string): string {
  const { from, to } = weekSpan(week);
  const [a, b] = [new Date(noonOf(from)), new Date(noonOf(to))];
  const start = `${MONTHS[a.getUTCMonth()]} ${a.getUTCDate()}`;
  return a.getUTCMonth() === b.getUTCMonth()
    ? `${start} – ${b.getUTCDate()}`
    : `${start} – ${MONTHS[b.getUTCMonth()]} ${b.getUTCDate()}`;
}

/** What the Sunday notification says under the name. */
export function recapBody(count: number): string {
  return `${count} ${DAILY_NAME}s from the week, all in one place`;
}

/** The recap that is out: the newest marked ready with something in it. */
export function latestReady(recaps: readonly RecapDoc[]): RecapDoc | null {
  return (
    [...recaps]
      .filter((recap) => recap.ready && (recap.entries?.length ?? 0) > 0)
      .sort((a, b) => b.week.localeCompare(a.week))[0] ?? null
  );
}

/** The copies in the order they happened. */
export function inOrder(entries: readonly RecapEntry[]): RecapEntry[] {
  return [...entries].sort((a, b) => a.day.localeCompare(b.day) || a.at - b.at);
}
